import React, { createContext, useContext, useEffect, useState } from 'react';
import { db } from '../lib/database';

const DatabaseContext = createContext();

export const useDatabase = () => {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  return context;
};

export const DatabaseProvider = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [initializationAttempts, setInitializationAttempts] = useState(0);

  useEffect(() => {
    const initDatabase = async () => {
      try {
        // Use the new ensureReady method which handles all initialization
        await db.ensureReady();
        setIsReady(true);
        console.log('Database initialized successfully');
      } catch (err) {
        console.error('Database initialization failed:', err);
        setError(err);
        
        // Retry logic (max 3 attempts)
        if (initializationAttempts < 3) {
          console.log(`Retrying database initialization (attempt ${initializationAttempts + 1}/3)...`);
          setTimeout(() => {
            setInitializationAttempts(prev => prev + 1);
          }, 1000);
        }
      }
    };

    initDatabase();
  }, [initializationAttempts]);

  const value = {
    db,
    isReady,
    error
  };

  // Show error state if database fails after retries
  if (error && initializationAttempts >= 3) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Database Error</h3>
            <p className="text-sm text-gray-600 mb-4">
              Unable to initialize the database. This may be due to browser restrictions or storage limits.
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Error: {error.message}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Reload Application
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
};
